import {
  FlattenFieldArrayItem,
  FlattenFieldArrayPayload,
  FlatteningFieldsProps,
  FlatteningFieldsRef,
  Mapping,
  ObjectConfig,
  ObjectConfigPayload,
  ValidationErrors
} from "./types/flattenFields";
import {
  AfterFanoutTransformationExampleRows,
  AfterFlattenTransformationExampleRows,
} from "./constants/transformationInputOutput";
import { DestinationSelectInputField } from "@/components/custom/InputField/WorkflowSelectInput";
import { NestedDropdown } from "@/components/custom/MultiLayeredDropdownField";
import { Button } from "@/components/ui/button";
import { FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ButtonVariant } from "@/constants/button/button";
import { INPUT_FIELD_CLASSNAME } from "@/constants/classname/classname";
import { AppFieldsType, CampArrayFieldOptions } from "@/constants/workflows/workflowCreate";
import { getAppFieldsAccordingToType } from "@/helpers/appFields";
import { RootState } from "@/redux";
import { cn } from "@/lib/utils";
import { Plus, Trash2 } from "lucide-react";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { InputFieldLabel } from "@/components/custom/InputField/Label";
import { getOrdinalWord, getSingularLabel } from "@/helpers/transformationLabels";

const generateUniqueId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const FlattenArrayFields = forwardRef<FlatteningFieldsRef, FlatteningFieldsProps>(({
  allFields,
  initialConfiguration,
}, ref) => {
  const { applications } = useSelector((state: RootState) => state.application);
  const [objectConfigs, setObjectConfigs] = useState<ObjectConfig[]>(() => {
    const arrayConfigs: ObjectConfigPayload[] = Array.isArray(initialConfiguration)
      ? initialConfiguration
      : initialConfiguration?.arrayConfigs ?? [];

    if (arrayConfigs.length > 0) {
      return arrayConfigs.map((config) => {
        const flattenArrayFieldName = config.flattenArrayFieldName || config.name || "";
        const fieldFlattenArrays: FlattenFieldArrayPayload[] = config.fieldFlattenArray && config.fieldFlattenArray.length > 0
          ? config.fieldFlattenArray
          : config.mapArray && config.mapArray.length > 0
            ? [{ mappings: config.mapArray }]
            : [];

        const normalizedFieldFlatteningArray: FlattenFieldArrayItem[] = fieldFlattenArrays.length > 0
          ? fieldFlattenArrays.map((fma) => {
            const rawMappings = fma.mappings && fma.mappings.length > 0
              ? fma.mappings
              : fma.mapArray && fma.mapArray.length > 0
                ? fma.mapArray
                : [];

            const mappings: Mapping[] = rawMappings.length > 0
              ? rawMappings.map((m) => {
                const fromValue = m.from;
                const toValue = m.to;
                return {
                  from: fromValue,
                  fromLabel: fromValue,
                  to: toValue,
                };
              })
              : [{ from: "", fromLabel: "", to: "" }];

            return {
              id: generateUniqueId('fieldFlatten'),
              mappings,
            };
          })
          : [{
            id: generateUniqueId('fieldFlatten'),
            mappings: [{ from: "", fromLabel: "", to: "" }]
          }];

        return {
          id: generateUniqueId('object'),
          flattenArrayFieldName,
          fieldFlattenArray: normalizedFieldFlatteningArray,
        };
      });
    }

    return [
      {
        id: generateUniqueId('object'),
        flattenArrayFieldName: "",
        fieldFlattenArray: [
          {
            id: generateUniqueId('fieldFlatten'),
            mappings: [
              {
                from: "",
                fromLabel: "",
                to: "",
              },
            ],
          },
        ],
      },
    ];
  });
  const [errors, setErrors] = useState<ValidationErrors>({
    objectConfigs: [{
      flattenArrayFieldName: false,
      fieldFlattenArray: [{
        mappings: [{ from: false, to: false }]
      }]
    }]
  });

  const buildTemplateMappings = (templates: Mapping[]) => {
    if (!templates.length) {
      return [{ from: "", fromLabel: "", to: "" }];
    }
    return templates.map(mapping => ({
      from: mapping.from ?? "",
      fromLabel: mapping.fromLabel ?? mapping.from ?? "",
      to: ""
    }));
  };

  const buildTemplateErrorMappings = (count: number) => {
    if (!count) {
      return [{ from: false, to: false }];
    }
    return Array.from({ length: count }, () => ({ from: false, to: false }));
  };

  const buildUnionTemplateMappings = (fieldFlattenArray: FlattenFieldArrayItem[]) => {
    if (!fieldFlattenArray.length) return [];
    const seen = new Set<string>();
    const template: Mapping[] = [];
    fieldFlattenArray.forEach(item => {
      item.mappings.forEach(mapping => {
        const source = (mapping.from ?? "").trim();
        if (!source) return;
        if (seen.has(source)) return;
        seen.add(source);
        template.push({
          from: mapping.from ?? "",
          fromLabel: mapping.fromLabel ?? mapping.from ?? "",
          to: ""
        });
      });
    });
    return template;
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      const newErrors: ValidationErrors = {
        objectConfigs: []
      };

      // Validate each object configuration
      objectConfigs.forEach((config) => {
        const configError = {
          flattenArrayFieldName: !config.flattenArrayFieldName.trim(),
          fieldFlattenArray: [] as Array<{
            mappings: Array<{ from: boolean; to: boolean }>;
          }>
        };

        // Validate each fieldFlattenArray item
        config.fieldFlattenArray.forEach((fieldFlatteningItem) => {
          const fieldFlatteningError = {
            mappings: [] as Array<{ from: boolean; to: boolean }>
          };

          // Validate each mapping
          fieldFlatteningItem.mappings.forEach((mapping) => {
            fieldFlatteningError.mappings.push({
              from: !mapping.from.trim(),
              to: !mapping.to.trim()
            });
          });

          configError.fieldFlattenArray.push(fieldFlatteningError);
        });

        newErrors.objectConfigs.push(configError);
      });

      setErrors(newErrors);

      // Return true if no errors
      const hasErrors = newErrors.objectConfigs.some(
        config => config.flattenArrayFieldName ||
          config.fieldFlattenArray.some(fma => fma.mappings.some(m => m.from || m.to))
      );

      return !hasErrors;
    },
    getData: () => {
      const arrayConfigs = objectConfigs.reduce<Array<{
        flattenArrayFieldName: string;
        fieldFlattenArray: Array<{ mappings: Array<{ from: string; to: string }> }>;
      }>>((acc, config) => {
        if (!config.flattenArrayFieldName.trim()) {
          return acc;
        }

        const fieldFlattenArray = config.fieldFlattenArray.reduce<Array<{ mappings: Array<{ from: string; to: string }> }>>((innerAcc, fieldFlatteningItem) => {
          const mappings = fieldFlatteningItem.mappings
            .filter(mapping => mapping.from.trim() && mapping.to.trim())
            .map(mapping => ({
              from: mapping.from.trim(),
              to: mapping.to.trim()
            }));

          if (mappings.length > 0) {
            innerAcc.push({ mappings });
          }

          return innerAcc;
        }, []);

        if (fieldFlattenArray.length > 0) {
          acc.push({
            flattenArrayFieldName: config.flattenArrayFieldName,
            fieldFlattenArray
          });
        }

        return acc;
      }, []);

      return {
        arrayConfigs
      };
    }
  }));

  const responseFieldTree = useMemo(
    () => getAppFieldsAccordingToType(allFields, AppFieldsType.RESPONSE_TYPE, applications),
    [allFields, applications]
  );

  return (
    <div className="text-primary flex flex-col max-h-[500px] min-h-[300px] h-full">
      <div className="flex-1 overflow-y-auto thin-scrollbar pr-2">
        <div className="flex w-full mb-4">
          <div className="w-1/2 flex-col items-start">
            <p className="text-sm font-medium">
              Example original rows
            </p>
            <div className="bg-foreground rounded-md border p-4 mt-1 max-h-[250px] overflow-auto thin-scrollbar">
              <pre className="text-xs">
                {JSON.stringify(AfterFanoutTransformationExampleRows, null, 2)}
              </pre>
            </div>
          </div>
          <div className="mx-1" />
          <div className="w-1/2">
            <p className="text-sm font-medium">Transformed rows flattening camper fields</p>
            <div className="bg-foreground rounded-md border p-4 mt-1 max-h-[250px] overflow-auto thin-scrollbar">
              <pre className="text-xs">
                {JSON.stringify(AfterFlattenTransformationExampleRows, null, 2)}
              </pre>
            </div>
          </div>
        </div>
        <div className="space-y-6">
          {objectConfigs.map((config, configIndex) => (
            <div key={config.id} className={cn(
              "rounded-md border p-4 shadow-sm",
              errors.objectConfigs[configIndex]?.flattenArrayFieldName ? "border-destructive" : ""
            )}>
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-1 max-w-[280px]">
                  <DestinationSelectInputField
                    label="Select a field to flatten"
                    labelDescription="e.g., parents, campers"
                    value={config.flattenArrayFieldName}
                    selectOptions={CampArrayFieldOptions}
                    onChange={(value) => {
                      setObjectConfigs((prev) =>
                        prev.map((item, idx) =>
                          idx === configIndex ? { ...item, flattenArrayFieldName: value } : item
                        )
                      );
                      // Clear error when selecting
                      setErrors(prev => ({
                        ...prev,
                        objectConfigs: prev.objectConfigs.map((err, idx) =>
                          idx === configIndex ? { ...err, flattenArrayFieldName: false } : err
                        )
                      }));
                    }}
                    additionalClassName="bg-foreground"
                    hasError={errors.objectConfigs[configIndex]?.flattenArrayFieldName}
                  />
                  {errors.objectConfigs[configIndex]?.flattenArrayFieldName && (
                    <p className="text-destructive text-xs mt-1">Object field name is required</p>
                  )}
                </div>
                {objectConfigs.length > 1 && (
                  <Button
                    variant={ButtonVariant.ICON}
                    className="ml-auto"
                    onClick={() => {
                      setObjectConfigs((prev) => prev.filter((_, idx) => idx !== configIndex));
                      setErrors(prev => ({
                        ...prev,
                        objectConfigs: prev.objectConfigs.filter((_, idx) => idx !== configIndex)
                      }));
                    }}
                  >
                    <Trash2 className="text-destructive" size={16} />
                  </Button>
                )}
              </div>

              {config.flattenArrayFieldName && (
                <>
                  <div className="mt-4">
                    <InputFieldLabel
                      label="Field Mapping Sets"
                      description="Define how fields from the array items should be mapped to new fields"
                    />
                    {config.fieldFlattenArray.map((fieldFlatteningItem, fieldFlatteningIndex) => (
                      <div key={fieldFlatteningItem.id} className="mb-4 rounded-md border border-accent bg-accent-background p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex flex-col items-start justify-center">
                            <FormLabel className="text-sm font-medium text-primary">
                              Mapping Set {fieldFlatteningIndex + 1}
                            </FormLabel>
                            <InputFieldLabel
                              description={`Assign new fieldname for ${getOrdinalWord(fieldFlatteningIndex)} ${getSingularLabel(config.flattenArrayFieldName).toLowerCase()}`}
                            />
                          </div>
                          {config.fieldFlattenArray.length > 1 && (
                            <Button
                              variant={ButtonVariant.ICON}
                              onClick={() => {
                                setObjectConfigs((prev) =>
                                  prev.map((objConfig, objIdx) => {
                                    if (objIdx !== configIndex) return objConfig;
                                    return {
                                      ...objConfig,
                                      fieldFlattenArray: objConfig.fieldFlattenArray.filter((_, fmIdx) => fmIdx !== fieldFlatteningIndex)
                                    };
                                  })
                                );
                                // Update errors
                                setErrors(prev => ({
                                  ...prev,
                                  objectConfigs: prev.objectConfigs.map((err, idx) =>
                                    idx === configIndex
                                      ? {
                                        ...err,
                                        fieldFlattenArray: err.fieldFlattenArray.filter((_, fmIdx) => fmIdx !== fieldFlatteningIndex)
                                      }
                                      : err
                                  )
                                }));
                              }}
                            >
                              <Trash2 className="text-destructive" size={16} />
                            </Button>
                          )}
                        </div>

                        {fieldFlatteningItem.mappings.map((mapping, mappingIndex) => (
                          <div key={`${fieldFlatteningItem.id}-mapping-${mappingIndex}`} className="flex flex-wrap items-start gap-4 mb-3">
                            <div className="flex-1 min-w-[240px] max-w-[280px]">
                              <NestedDropdown
                                data={responseFieldTree}
                                field={mapping.from}
                                label="From field"
                                description="Select the source field"
                                initialSelectedLabel={mapping.fromLabel || mapping.from}
                                onSelect={(item) => {
                                  setObjectConfigs((prev) =>
                                    prev.map((objConfig, objIdx) => {
                                      if (objIdx !== configIndex) return objConfig;
                                      return {
                                        ...objConfig,
                                        fieldFlattenArray: objConfig.fieldFlattenArray.map((fmItem, fmIdx) => {
                                          if (fmIdx !== fieldFlatteningIndex) return fmItem;
                                          return {
                                            ...fmItem,
                                            mappings: fmItem.mappings.map((map, mapIdx) =>
                                              mapIdx === mappingIndex
                                                ? { ...map, from: item.codeChain, fromLabel: item.displayName }
                                                : map
                                            )
                                          };
                                        })
                                      };
                                    })
                                  );
                                  // Clear error
                                  setErrors(prev => ({
                                    ...prev,
                                    objectConfigs: prev.objectConfigs.map((err, idx) =>
                                      idx === configIndex
                                        ? {
                                          ...err,
                                          fieldFlattenArray: err.fieldFlattenArray.map((fmErr, fmIdx) =>
                                            fmIdx === fieldFlatteningIndex
                                              ? {
                                                ...fmErr,
                                                mappings: fmErr.mappings.map((mErr, mIdx) =>
                                                  mIdx === mappingIndex ? { ...mErr, from: false } : mErr
                                                )
                                              }
                                              : fmErr
                                          )
                                        }
                                        : err
                                    )
                                  }));
                                }}
                                showErrorFields={errors.objectConfigs[configIndex]?.fieldFlattenArray[fieldFlatteningIndex]?.mappings[mappingIndex]?.from}
                              />
                              {errors.objectConfigs[configIndex]?.fieldFlattenArray[fieldFlatteningIndex]?.mappings[mappingIndex]?.from && (
                                <p className="text-destructive text-xs">From field cannot be empty</p>
                              )}
                            </div>
                            <div className="flex-1 min-w-[240px] max-w-[280px]">
                              <InputFieldLabel
                                label="To field"
                                description="New field name for the mapped value"
                              />
                              <Input
                                className={cn(
                                  INPUT_FIELD_CLASSNAME,
                                  "bg-foreground",
                                  errors.objectConfigs[configIndex]?.fieldFlattenArray[fieldFlatteningIndex]?.mappings[mappingIndex]?.to && "border-destructive bg-destructive-foreground"
                                )}
                                type="text"
                                value={mapping.to}
                                placeholder="e.g., leadName"
                                onChange={(event) => {
                                  const { value } = event.target;
                                  setObjectConfigs((prev) =>
                                    prev.map((objConfig, objIdx) => {
                                      if (objIdx !== configIndex) return objConfig;
                                      return {
                                        ...objConfig,
                                        fieldFlattenArray: objConfig.fieldFlattenArray.map((fmItem, fmIdx) => {
                                          if (fmIdx !== fieldFlatteningIndex) return fmItem;
                                          return {
                                            ...fmItem,
                                            mappings: fmItem.mappings.map((map, mapIdx) =>
                                              mapIdx === mappingIndex ? { ...map, to: value } : map
                                            )
                                          };
                                        })
                                      };
                                    })
                                  );
                                  // Clear error
                                  setErrors(prev => ({
                                    ...prev,
                                    objectConfigs: prev.objectConfigs.map((err, idx) =>
                                      idx === configIndex
                                        ? {
                                          ...err,
                                          fieldFlattenArray: err.fieldFlattenArray.map((fmErr, fmIdx) =>
                                            fmIdx === fieldFlatteningIndex
                                              ? {
                                                ...fmErr,
                                                mappings: fmErr.mappings.map((mErr, mIdx) =>
                                                  mIdx === mappingIndex ? { ...mErr, to: false } : mErr
                                                )
                                              }
                                              : fmErr
                                          )
                                        }
                                        : err
                                    )
                                  }));
                                }}
                              />
                              {errors.objectConfigs[configIndex]?.fieldFlattenArray[fieldFlatteningIndex]?.mappings[mappingIndex]?.to && (
                                <p className="text-destructive text-xs">Specify new field name for the mapped value</p>
                              )}
                            </div>
                            {fieldFlatteningItem.mappings.length > 1 && (
                              <Button
                                variant={ButtonVariant.ICON}
                                className="mt-10"
                                onClick={() => {
                                  setObjectConfigs((prev) =>
                                    prev.map((objConfig, objIdx) => {
                                      if (objIdx !== configIndex) return objConfig;
                                      return {
                                        ...objConfig,
                                        fieldFlattenArray: objConfig.fieldFlattenArray.map((fmItem, fmIdx) => {
                                          if (fmIdx !== fieldFlatteningIndex) return fmItem;
                                          return {
                                            ...fmItem,
                                            mappings: fmItem.mappings.filter((_, mapIdx) => mapIdx !== mappingIndex)
                                          };
                                        })
                                      };
                                    })
                                  );
                                  // Update errors
                                  setErrors(prev => ({
                                    ...prev,
                                    objectConfigs: prev.objectConfigs.map((err, idx) =>
                                      idx === configIndex
                                        ? {
                                          ...err,
                                          fieldFlattenArray: err.fieldFlattenArray.map((fmErr, fmIdx) =>
                                            fmIdx === fieldFlatteningIndex
                                              ? {
                                                ...fmErr,
                                                mappings: fmErr.mappings.filter((_, mIdx) => mIdx !== mappingIndex)
                                              }
                                              : fmErr
                                          )
                                        }
                                        : err
                                    )
                                  }));
                                }}
                              >
                                <Trash2 className="text-destructive" size={16} />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          className="font-semibold text-accent border-accent bg-background hover:text-accent hover:border-accent"
                          variant={ButtonVariant.OUTLINE}
                          onClick={() => {
                            setObjectConfigs((prev) =>
                              prev.map((objConfig, objIdx) =>
                                objIdx === configIndex
                                  ? {
                                    ...objConfig,
                                    fieldFlattenArray: objConfig.fieldFlattenArray.map((fmItem, fmIdx) =>
                                      fmIdx === fieldFlatteningIndex
                                        ? {
                                          ...fmItem,
                                          mappings: [
                                            ...fmItem.mappings,
                                            { from: "", fromLabel: "", to: "" }
                                          ]
                                        }
                                        : fmItem
                                    )
                                  }
                                  : objConfig
                              )
                            );
                            // Add error entry
                            setErrors(prev => ({
                              ...prev,
                              objectConfigs: prev.objectConfigs.map((err, idx) =>
                                idx === configIndex
                                  ? {
                                    ...err,
                                    fieldFlattenArray: err.fieldFlattenArray.map((fmErr, fmIdx) =>
                                      fmIdx === fieldFlatteningIndex
                                        ? {
                                          ...fmErr,
                                          mappings: [...fmErr.mappings, { from: false, to: false }]
                                        }
                                        : fmErr
                                    )
                                  }
                                  : err
                              )
                            }));
                          }}
                        >
                          <Plus className="mr-2 w-4 h-4" />
                          Add another field mapping
                        </Button>
                      </div>
                    ))}

                    <Button
                      type="button"
                      className="font-semibold text-accent border-accent bg-background hover:text-accent hover:border-accent"
                      variant={ButtonVariant.OUTLINE}
                      onClick={() => {
                        const currentConfig = objectConfigs[configIndex];
                        const unionTemplate = buildUnionTemplateMappings(currentConfig?.fieldFlattenArray ?? []);
                        const templateMappings = buildTemplateMappings(unionTemplate);
                        const normalizedTemplate = templateMappings.length
                          ? templateMappings
                          : [{ from: "", fromLabel: "", to: "" }];
                        const templateErrors = buildTemplateErrorMappings(normalizedTemplate.length);

                        setObjectConfigs(prev =>
                          prev.map((objConfig, objIdx) =>
                            objIdx === configIndex
                              ? {
                                ...objConfig,
                                fieldFlattenArray: [
                                  ...objConfig.fieldFlattenArray,
                                  {
                                    id: generateUniqueId('fieldFlatten'),
                                    mappings: normalizedTemplate
                                  }
                                ]
                              }
                              : objConfig
                          )
                        );

                        setErrors(prev => ({
                          ...prev,
                          objectConfigs: prev.objectConfigs.map((err, idx) =>
                            idx === configIndex
                              ? {
                                ...err,
                                fieldFlattenArray: [
                                  ...err.fieldFlattenArray,
                                  { mappings: templateErrors }
                                ]
                              }
                              : err
                          )
                        }));
                      }}
                    >
                      <Plus className="mr-2 w-4 h-4" />
                      Add another mapping set
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <Button
          type="button"
          className="font-semibold text-accent border-accent bg-background hover:text-accent hover:border-accent"
          variant={ButtonVariant.OUTLINE}
          onClick={() => {
            setObjectConfigs((prev) => [
              ...prev,
              {
                id: generateUniqueId('object'),
                flattenArrayFieldName: "",
                fieldFlattenArray: [
                  {
                    id: generateUniqueId('fieldFlatten'),
                    mappings: [{ from: "", fromLabel: "", to: "" }]
                  }
                ]
              }
            ]);
            // Add error entry for new object config
            setErrors(prev => ({
              ...prev,
              objectConfigs: [
                ...prev.objectConfigs,
                {
                  flattenArrayFieldName: false,
                  fieldFlattenArray: [{ mappings: [{ from: false, to: false }] }]
                }
              ]
            }));
          }}
        >
          <Plus className="mr-2 w-4 h-4" />
          Add another object mapping
        </Button>
      </div>
    </div>
  );
});
